import React, {useEffect, useMemo, useState} from "react";
import {instance} from "../../../API/API";
import {expenseCategoriesApi} from "./ExpensesSettings";
import {Button, Card, ConfigProvider, DatePicker, Input, message, Popconfirm, Select, Table, Tag, Tooltip} from "antd";
import dayjs from "dayjs";
import style from "./subcomponents/styles/expenses.periodic.module.css";
import EuroFormatter from "./_helpers/EuroFormatter";
import {DeleteOutlined, EditOutlined, ReloadOutlined, SearchOutlined} from "@ant-design/icons";
import ruRU from "antd/locale/ru_RU";
import AddOneExpenseModal from "./subcomponents/add_one_expense_modal/AddOneExpenseModal";

const {RangePicker} = DatePicker;

const ExpensesOne = props => {
    const [modalVisible, setModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [categories, setCategories] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    // Фильтры
    const [searchText, setSearchText] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
    const [dateRange, setDateRange] = useState([
        dayjs().startOf('month'),
        dayjs().endOf('month')
    ]);

    //API endpoints
    const api = {
        getAll: (params = {}) => {
            const queryParams = new URLSearchParams();

            if (params.category_id && params.category_id !== 'all') {
                queryParams.append('category_id', params.category_id);
            }

            if (params.date_from) {
                queryParams.append('date_from', params.date_from);
            }

            if (params.date_to) {
                queryParams.append('date_to', params.date_to);
            }

            const queryString = queryParams.toString();
            return instance.get(`/finance_04122025/get_one_expenses.php${queryString ? '?' + queryString : ''}`)
                .then(res => res.data);
        },
        create: (data) => instance.post(`/finance_04122025/create_one_expense.php`, data).then(res => res.data),
        update: (id, data) => instance.put(`/finance_04122025/update_one_expense.php?id=${id}`, data).then(res => res.data),
        delete: (id) => instance.delete(`/finance_04122025/delete_one_expense.php?id=${id}`).then(res => res.data)
    }

    const getCategories = async () => {
        try {
            const data = await expenseCategoriesApi.getAll();
            setCategories(data.data);
        } catch (error) {
            message.error('Ошибка загрузки категорий');
        }
    };

    const getExpenses = async () => {
        setLoading(true);
        try {
            const params = {};

            if (categoryFilter && categoryFilter !== 'all') {
                params.category_id = categoryFilter;
            }

            // Добавляем фильтры по дате
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.date_from = dateRange[0].format('YYYY-MM-DD');
                params.date_to = dateRange[1].format('YYYY-MM-DD');
            }

            const response = await api.getAll(params);
            if (response.success) {
                setExpenses(response.data.sort((a, b) => a.id - b.id));
            } else {
                message.error(response.error || 'Ошибка загрузки расходов');
            }
        } catch (error) {
            console.error('Error fetching expenses:', error);
            message.error('Ошибка подключения к серверу');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getCategories();
    }, []);

    useEffect(() => {
        getExpenses();
    }, [categoryFilter, dateRange]);

    // Фильтрация данных на клиенте (для поиска и типа оплаты)
    const filteredData = useMemo(() => {
        return expenses.filter(item => {
            // Поиск по названию и описанию
            const matchesSearch = searchText === '' ||
                item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.description?.toLowerCase().includes(searchText.toLowerCase());

            // Фильтр по типу оплаты
            const matchesPaymentType = paymentTypeFilter === 'all' ||
                (paymentTypeFilter === 'cash' && item.payment_type === 1) ||
                (paymentTypeFilter === 'non_cash' && item.payment_type === 2);

            return matchesSearch && matchesPaymentType;
        });
    }, [expenses, searchText, paymentTypeFilter]);

    const handleDelete = async (record) => {
        try {
            const response = await api.delete(record.id);
            if (response.success) {
                message.success('Расход успешно удален');
                getExpenses();
            } else {
                message.error(response.error || 'Ошибка удаления');
            }
        } catch (error) {
            message.error('Ошибка подключения к серверу');
        }
    };

    const handleCreate = async (data) => {
        console.log(data);
        try {
            // Преобразуем данные для API
            const apiData = {
                name: data.name,
                description: data.description || '',
                category_id: parseInt(data.category_id),
                amount: parseFloat(data.amount),
                is_cash: data.isCash ? 1 : 2,
                tax_rate: data.isCash ? 0 : data.tax_rate,
                date: data.date ? data.date : dayjs().format('DD.MM.YYYY'),
            };
            const response = await api.create(apiData);
            if (response.success) {
                message.success('Расход успешно создан');
                setModalVisible(false);
                getExpenses();
            } else {
                message.error(response.error || 'Ошибка создания');
            }
        } catch (error) {
            console.error('Error creating expense:', error);
            message.error('Ошибка подключения к серверу');
        }
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        setEditModalVisible(true);
    };

    const handleUpdate = async (data) => {
        console.log(data);
        try {
            const apiData = {
                id: data.id,
                name: data.name,
                description: data.description || '',
                category_id: parseInt(data.category_id),
                amount: parseFloat(data.amount),
                payment_type: data.isCash ? 1 : 2,
                tax_rate: data.isCash ? 0 : data.tax_rate,
                date: data.date || 'monthly',
            };

            const response = await api.update(editingRecord.id, apiData);
            if (response.success) {
                message.success('Расход успешно обновлен');
                setEditModalVisible(false);
                setEditingRecord(null);
                getExpenses();
            } else {
                message.error(response.error || 'Ошибка обновления');
            }
        } catch (error) {
            message.error('Ошибка подключения к серверу');
        }
    };

    // Обработчики фильтров
    const handleSearch = (value) => setSearchText(value);
    const handleCategoryChange = (value) => setCategoryFilter(value);
    const handlePaymentTypeChange = (value) => setPaymentTypeFilter(value);

    const handleDateRangeChange = (dates) => {
        if (dates) {
            setDateRange([dates[0].startOf('day'), dates[1].endOf('day')]);
        } else {
            // Сброс на текущий месяц
            setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
        }
    };

    const handleQuickDateSelect = (period) => {
        const today = dayjs();
        let startDate, endDate;

        switch (period) {
            case 'today':
                startDate = today.startOf('day');
                endDate = today.endOf('day');
                break;
            case 'yesterday':
                startDate = today.subtract(1, 'day').startOf('day');
                endDate = today.subtract(1, 'day').endOf('day');
                break;
            case 'week':
                startDate = today.startOf('week');
                endDate = today.endOf('week');
                break;
            case 'month':
                startDate = today.startOf('month');
                endDate = today.endOf('month');
                break;
            case 'year':
                startDate = today.startOf('year');
                endDate = today.endOf('year');
                break;
            case 'lastMonth':
                startDate = today.subtract(1, 'month').startOf('month');
                endDate = today.subtract(1, 'month').endOf('month');
                break;
            case 'lastYear':
                startDate = today.subtract(1, 'year').startOf('year');
                endDate = today.subtract(1, 'year').endOf('year');
                break;
            case 'all':
                // Для "все время" не устанавливаем диапазон дат
                setDateRange(null);
                return;
            default:
                startDate = today.startOf('month');
                endDate = today.endOf('month');
        }

        setDateRange([startDate, endDate]);
    };

    const handleResetFilters = () => {
        setSearchText('');
        setCategoryFilter('all');
        setPaymentTypeFilter('all');
        setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
    };

    const getPaymentTypeLabel = (payment_type) => {
        return payment_type === 1 ? 'Наличный' : 'Безналичный';
    };

    const getCategoryNameById = (id) => {
        const category = categories ? categories.find(el => +el.id === +id) : null;
        const name = category ? category.name : '';
        const color = category ? category.color : '';
        return {name, color};
    };

    // Функция для форматирования отображаемого диапазона дат
    const formatDateRange = () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            return 'Все время';
        }
        return `${dateRange[0].format('DD.MM.YYYY')} - ${dateRange[1].format('DD.MM.YYYY')}`;
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            align: 'center',
            width: 50,
            render: (text, record) => <Tag color={'blue'}>{text}</Tag>
        },
        {
            title: 'Дата',
            dataIndex: 'date',
            key: 'date',
            width: 100,
            render: (text, record) => {
                return (
                    <div style={{fontSize: '12px'}}>
                        <div>{dayjs(text).format('HH:mm')}</div>
                        <div>{dayjs(text).format('YYYY-MM-DD')}</div>
                    </div>
                )
            }
        },
        {
            title: 'Наименование',
            dataIndex: 'name',
            key: 'name',
            align: 'left',

            render: (text, record) => {
                const {name, color} = getCategoryNameById(record.category_id);

                return (
                    <div>
                        <span className={style.categoty_tag_second}>
                            <Tag variant={'outlined'} color={color} key={record.id}>
                                {name}
                            </Tag>
                        </span>
                        <div style={{fontWeight: 600}}>{text}</div>
                        <div style={{color: '#727272', fontSize: '12px'}}>{record.description}</div>
                    </div>
                )
            }
        },
        {
            title: 'Сумма',
            dataIndex: 'amount',
            key: 'amount',
            width: 150,
            render: (text, record) => {
                return (
                    <div style={{width: '100%'}}>
                        <span style={{fontSize: '16px', fontWeight: 600, color: '#444444'}}><EuroFormatter
                            amount={record.total_amount_with_tax}/></span>
                        {
                            record.payment_type !== 1 && (record.tax_rate !== "0.00" && +record.tax_rate !== 0) && (
                                <div style={{fontSize: '12px', color: '#888'}}>
                                    (в т.ч. НДС: <EuroFormatter
                                    amount={(record.total_amount_with_tax - (record.total_amount_with_tax / 1.21))}/>)
                                </div>
                            )
                        }
                    </div>
                )
            }
        },
        {
            title: 'Тип оплаты',
            dataIndex: 'payment_type',
            key: 'payment_type',
            width: 100,
            render: (text) => (
                <Tag color={text === 1 ? 'green' : 'blue'}>
                    {getPaymentTypeLabel(text)}
                </Tag>
            )
        },
        {
            title: '',
            key: 'actions',
            align: 'center',
            width: 80,
            render: (text, record) => (
                <div className={style.container_icons}>
                    <Tooltip title="Редактировать">
                        <EditOutlined
                            style={{color: '#888888', fontSize: '16px', cursor: 'pointer', margin: '0 8px'}}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Удалить запись?"
                        description="Вы уверены, что хотите удалить эту запись?"
                        onConfirm={() => handleDelete(record)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Tooltip title="Удалить">
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined/>}
                            />
                        </Tooltip>
                    </Popconfirm>
                </div>
            )
        }
    ];
    console.log(filteredData);
    return (
        <div>
            <ConfigProvider locale={ruRU}>
                <div className={style.add_btn_cont}>
                    <Button type="primary" onClick={() => setModalVisible(true)}>
                        Добавить
                    </Button>
                </div>

                <Card style={{marginBottom: 16}}>
                    <div className={style.filter_container}>
                        <div>
                            <Input
                                placeholder="Поиск по названию и описанию..."
                                prefix={<SearchOutlined/>}
                                value={searchText}
                                onChange={(e) => handleSearch(e.target.value)}
                                allowClear
                                style={{width: '200px'}}
                            />
                        </div>
                        <div style={{width: '200px'}}>
                            <Select
                                placeholder="Категория"
                                value={categoryFilter}
                                onChange={handleCategoryChange}
                                allowClear
                                loading={!categories}
                                style={{width: '200px'}}
                            >
                                <Select.Option value="all">Все категории</Select.Option>
                                {categories?.map(category => (
                                    <Select.Option key={category.id} value={category.id}>
                                        {category.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                        <div>
                            <Select
                                placeholder="Тип оплаты"
                                value={paymentTypeFilter}
                                onChange={handlePaymentTypeChange}
                                allowClear
                                style={{width: '200px'}}
                            >
                                <Select.Option value="all">Все типы</Select.Option>
                                <Select.Option value="cash">Наличный</Select.Option>
                                <Select.Option value="non_cash">Безналичный</Select.Option>
                            </Select>
                        </div>
                        <div>
                            <Select
                                placeholder="Быстрый выбор"
                                style={{width: '200px'}}
                                onChange={handleQuickDateSelect}
                                defaultValue="month"
                            >
                                <Select.Option value="today">Сегодня</Select.Option>
                                <Select.Option value="yesterday">Вчера</Select.Option>
                                <Select.Option value="week">Эта неделя</Select.Option>
                                <Select.Option value="month">Этот месяц</Select.Option>
                                <Select.Option value="year">Этот год</Select.Option>
                                <Select.Option value="lastMonth">Прошлый месяц</Select.Option>
                                <Select.Option value="lastYear">Прошлый год</Select.Option>
                                <Select.Option value="all">Все время</Select.Option>
                            </Select>
                        </div>
                        <div>
                            <RangePicker
                                value={dateRange}
                                onChange={handleDateRangeChange}
                                format="DD.MM.YYYY"
                                style={{width: '220px'}}
                                placeholder={['Начало', 'Конец']}
                                allowClear={false}
                            />
                        </div>
                        <div>
                            <Button
                                icon={<ReloadOutlined/>}
                                onClick={handleResetFilters}
                                style={{width: '200px'}}
                            >
                                Сбросить
                            </Button>
                        </div>
                    </div>
                    <div className={style.description_text}>
                        <div>
                            <Tag color="processing">Период: {formatDateRange()}</Tag>
                            <Tag color="processing">Всего записей: {expenses.length}</Tag>
                        </div>
                        <div>
                            <Tag color="success">Найдено: {filteredData.length}</Tag>
                        </div>
                    </div>
                </Card>

                <Table
                    dataSource={filteredData}
                    columns={columns}
                    rowKey="id"
                    bordered={true}
                    size={'small'}
                    loading={loading}
                    pagination={false}
                />

                {/* Модальное окно создания */}
                <AddOneExpenseModal
                    visible={modalVisible}
                    editMode={false}
                    onCancel={() => setModalVisible(false)}
                    onSave={handleCreate}
                    categories={categories || []}
                    title="Добавить разовый расход"
                />

                {/* Модальное окно редактирования */}
                {editingRecord && (
                    <AddOneExpenseModal
                        visible={editModalVisible}
                        editMode={true}
                        onCancel={() => {
                            setEditModalVisible(false);
                            setEditingRecord(null);
                        }}
                        onSave={handleUpdate}
                        categories={categories || []}
                        title="Редактировать разовый расход"
                        initialValues={{
                            name: editingRecord.name,
                            description: editingRecord.description,
                            category: editingRecord.category_id,
                            amount: editingRecord.amount,
                            isCash: editingRecord.payment_type === 1,
                            tax: editingRecord.tax_rate,
                            date: editingRecord.date,
                        }}
                    />
                )}
            </ ConfigProvider>
        </div>
    );
}

export default ExpensesOne